import { CurrencyPipe, DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from 'file-saver';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

import { environment } from '@app/env-service';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { DecimalPlacesUtils } from 'v2/modules/shared/util/decimal-places.utils';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

@Injectable({
  providedIn: 'root',
})
export class ReceivingReportService {
  url :string = environment.url;
  clientConfig :any = environment.clientConfig;

  curPipe :CurrencyPipe;
  datePipe :DatePipe;

  excelFileType =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  excelFileExtension = '.xlsx';

  private saveExcelFile(buffer :any, fileName :string, ext ?:string) :void {
    const data :Blob = new Blob([buffer], { type: this.excelFileType });
    FileSaver.saveAs(data, fileName + (ext ? ext : this.excelFileExtension));
  }

  private readonly _receivingReports = new BehaviorSubject<any>(<any>[]);
  public receivingReports$ = this._receivingReports.asObservable();

  public gettingRecords :boolean = false;

  constructor(
    private translate :TranslateService,
    private http :HttpClient,
    private ffService :FeatureToggleService,
  ) {
    this.curPipe = new CurrencyPipe('en');
    this.datePipe = new DatePipe('en');
  }

  // po list
  get(params) {
    this.gettingRecords = true;

    return this.http
      .get(`${this.url}/mt_receiving_reports`, { params: params })
      .subscribe((resp :any) => {
        this.gettingRecords = false;
        this._receivingReports.next(resp);
      });
  }

  getAsync(params) {
    return this.http.get(`${this.url}/mt_receiving_reports`, {
      params: params,
    });
  }
  // form po
  updateReceivingReport(
    receivingReportId :number,
    orderData :any,
  ) :Observable<any> {
    return this.http
      .post(`${this.url}/mt_receiving_reports/${receivingReportId}`, orderData)
      .pipe(
        tap((resp :any) => {
          this.tryUpdateOrAddResp(resp);
        }),
      );
  }

  createReceivingReport(orderData :any) :Observable<any> {
    return this.http.post(`${this.url}/mt_receiving_reports`, orderData).pipe(
      tap((resp :any) => {
        setTimeout(() => {
          const receivingReports :any = this._receivingReports.getValue();
          if (receivingReports && receivingReports.data) {
            receivingReports.data.splice(0, 0, resp);
            this._receivingReports.next(receivingReports);
          }
        });
        this.reset();
      }),
    );
  }

  deleteReceivingReport(receivingReportId :number) {
    return this.http
      .delete(`${this.url}/mt_receiving_reports/${receivingReportId}`)
      .pipe(
        tap((resp :any) => {
          const receivingReports :any = this._receivingReports.getValue();
          const poIdx = receivingReports.data.findIndex(
            (row) => row.id == receivingReportId,
          );
          receivingReports.data.splice(poIdx, 1);
          this._receivingReports.next(receivingReports);
          this.reset();
        }),
      );
  }

  tryUpdateOrAddResp(resp) {
    const receivingReports :any = this._receivingReports.getValue();
    const poIdx = receivingReports.data.findIndex((row) => row.id == resp.id);
    if (poIdx >= 0) {
      receivingReports.data[poIdx] = resp;
    } else {
      receivingReports.data.splice(0, 0, resp);
    }
    this._receivingReports.next(receivingReports);
    this.reset();
  }

  deleteReceivingReportFile(receivingReportId :number, fileId :number) {
    return this.http
      .delete(
        `${this.url}/mt_receiving_reports/${receivingReportId}/files/${fileId}`,
      )
      .pipe(
        tap((resp :any) => {
          this.tryUpdateOrAddResp(resp);
        }),
      );
  }

  reset() {}

  exportRFQtoPDF(receivingReport :any) {
    const logo = environment.clientConfig.assetImgDir + 'logo-print.png';
    let temp_logo = null;
    try {
      temp_logo = receivingReport.companyLogo;
    } catch (e) {}
    if (
      (temp_logo && temp_logo.startsWith('data:image/webp;base64,')) ||
      (temp_logo && temp_logo.startsWith('data:image/png;base64,'))
    ) {
      // Process and then create PDF
      this.processPNG(temp_logo, (convertedLogo :any) => {
        this.createPDF(receivingReport, convertedLogo);
      });
    } else {
      // If temp_logo is not set, use the default logo
      this.getImgDataUrlValue(logo, (imgUri :any) => {
        this.createPDF(receivingReport, imgUri);
      });
    }
  }

  createPDF(receivingReport, logoUri) {
    // prepare table data
    const tableData :any[] = [
      [
        { text: 'Line', bold: true },
        { text: 'RFQ/Item', bold: true },
        { text: 'Quantity', bold: true },
        { text: 'UOM', bold: true },
        { text: 'Unit Price', bold: true },
        { text: 'Line Total', bold: true },
      ],
    ];

    receivingReport.receiving_report_rfq.forEach((rfq, idx) => {
      const uom = rfq?.purchase_order_rfq?.rfq?.unit_of_measures?.find(
        (u) => u.id === rfq?.purchase_order_rfq?.ordered_uom_id,
      );
      const data = [
        idx + 1,
        {
          text:
            'PO#' +
            rfq.purchase_order_id +
            ' ' +
            rfq?.stock?.warehouse.location_name +
            ' [' +
            rfq?.purchase_order_rfq?.internal_sku +
            ']' +
            rfq?.purchase_order_rfq?.product_name,
        },
        { text: rfq.quantity, alignment: 'right' },
        { text: uom?.name, alignment: 'right' },
        {
          text: this.curPipe.transform(rfq.unit_price, ''),
          alignment: 'right',
        },
        {
          text: this.curPipe.transform(rfq.line_total, ''),
          alignment: 'right',
        },
      ];
      tableData.push(data);
    });

    // prepare header
    const headerContent = [
      {
        columns: [
          {
            alignment: 'left',
            image: logoUri,
            width: 250,
            style: 'logo',
          },
          [
            {
              text: 'Receiving Report',
              alignment: 'right',
              style: 'header',
            },
            {
              text:
                'Received Date:  ' +
                this.datePipe.transform(
                  receivingReport.received_date,
                  'mediumDate',
                ),
              alignment: 'right',
            },
            {
              text: 'Tracking #:  ' + receivingReport.tracking_number,
              alignment: 'right',
            },
          ],
        ],
      },
    ];

    const footerContent = [
      {
        columns: [
          { text: 'Total Units:', alignment: 'right' },
          {
            text: receivingReport.total_quantity,
            alignment: 'right',
            width: 120,
          },
        ],
      },
      {
        columns: [
          { text: 'Total Cost:', alignment: 'right' },
          {
            text: this.curPipe.transform(receivingReport.total_cost),
            alignment: 'right',
            width: 120,
          },
        ],
      },
    ];

    const tableContent :any[] = [
      {
        text: 'Purchase order items / rfqs',
        alignment: 'left',
        style: 'header',
      },
      {
        style: 'table',
        table: {
          widths: ['auto', '*', 'auto', 'auto', 'auto', 'auto'],
          headerRows: 1,
          body: tableData,
        },
        // layout: 'headerLineOnly'
      },
    ];

    let pdfContent :any[] = [];

    pdfContent = pdfContent.concat(headerContent, tableContent, footerContent);

    const documentDefinition = {
      pageSize: 'letter',
      pageOrientation: 'portrait',
      pageMargins: [0, 40, 20, 40], // [left, top, right, bottom]
      content: pdfContent,

      defaultStyle: {
        fontSize: 10,
        columnGap: 20,
      },
      styles: {
        logo: {
          fillColor: '#ee0000',
        },
        product: {
          fontSize: 15,
          margin: [0, 20, 0, 5],
          bold: true,
        },
        category: {
          fontSize: 14,
        },
        shipping: {
          margin: [0, 25, 0, 0],
        },
        header: {
          fontSize: 16,
          bold: false,
          margin: [0, 30, 0, 5],
          padding: [0, 0, 0, 0],
        },
        subheader: {
          fontSize: 14,
          bold: false,
          margin: [0, 0, 0, 0],
          padding: [0, 0, 0, 0],
        },
        table: {
          margin: [0, 5, 0, 15],
        },
        tableHeader: {
          bold: true,
          fontSize: 13,
          color: 'black',
        },
      },
    };
    Swal.close();
    pdfMake.createPdf(documentDefinition).download('ReceivingReport.pdf');
  }
  exportListtoCSV(receivingReports :any) {
    const fileName :string = 'ReceivingReport';
    const jsonData :any = [];

    receivingReports.forEach((rr) => {
      let rrDate :any = '';
      try {
        rrDate = this.datePipe.transform(rr.received_date, 'MM/dd/yyyy');
      } catch (e) {
        rrDate = '';
      }

      // Loop through product lines
      if (rr.receiving_report_rfq) {
        rr.receiving_report_rfq.forEach((rfq :any) => {
          if (rfq.purchase_order_rfq) {
            let rrPoTags :string[] = [];
            try {
              rfq.tags.forEach((tag) => {
                rrPoTags.push(tag.name);
              });
            } catch (e) {
              rrPoTags = [''];
            }

            jsonData.push({
              'Transaction #': rr.id.toString(),
              'Item Type': 'Product',
              'Received Date': rrDate,
              Tracking: rr.tracking_number,
              'Received By': rr.received_by,
              'PO #': rfq.purchase_order_id.toString(),
              SKU: rfq.purchase_order_rfq.internal_sku,
              'Product Name': rfq.purchase_order_rfq.product_name,
              'Product Dept': rfq.purchase_order_rfq.rfq_dept
                ? rfq.purchase_order_rfq.rfq_dept.product_dept
                : '',
              'Unit Price': rfq.unit_price,
              'Received Qty': rfq.quantity,
              'Line Total': this.curPipe.transform(rfq.line_total, ''),
              'Total Units': rr.total_quantity,
              'Total Cost': this.curPipe.transform(rr.total_cost, ''),
              'Tag(s)': rrPoTags.join(', '),
            });
          }
        });
      }

      // Add extra cost line item, if any
      if (rr.extra_costs && Array.isArray(rr.extra_costs)) {
        rr.extra_costs.forEach((cost :any) => {
          jsonData.push({
            'Transaction #': rr.id.toString(),
            'Item Type': 'Extra Cost',
            'Received Date': rrDate,
            Tracking: rr.tracking_number,
            'Received By': rr.received_by,
            'PO #': '', // optional: use cost.po_id if available
            SKU: '', // or "EXTRA-COST" if you want
            'Product Name': `${cost.name} - ${cost.cost_allocation_type}`,
            'Product Dept': '',
            'Unit Price': cost.unit_price,
            'Received Qty': '',
            'Line Total': this.curPipe.transform(cost.line_total, ''),
            'Total Units': DecimalPlacesUtils.format(cost.quantity),
            'Total Cost': this.curPipe.transform(cost.line_total, ''),
            'Tag(s)': '',
          });
        });
      }
    });

    const recordsSheet :XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);

    const wb :XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, recordsSheet, 'Records');

    const excelBuffer :any = XLSX.write(wb, {
      bookType: 'csv',
      type: 'array',
    });

    Swal.close();

    this.saveExcelFile(excelBuffer, fileName, '.csv');
  }

  exportListtoPDF(receivingReports :any) {
    const logo = environment.clientConfig.assetImgDir + 'logo-print.png';
    let temp_logo = null;
    try {
      temp_logo = receivingReports.companyLogo;
    } catch (e) {}
    if (
      (temp_logo && temp_logo.startsWith('data:image/webp;base64,')) ||
      (temp_logo && temp_logo.startsWith('data:image/png;base64,'))
    ) {
      // Process and then create PDF
      this.processPNG(temp_logo, (convertedLogo :any) => {
        this.creatListPDF(receivingReports, convertedLogo);
      });
    } else {
      // If temp_logo is not set, use the default logo
      this.getImgDataUrlValue(logo, (imgUri :any) => {
        this.creatListPDF(receivingReports, imgUri);
      });
    }
  }

  processPNG(dataUrl :string, callback :(pngUrl :string) => void) {
    const img = new Image();
    img.onload = function () {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);

      // Convert to PNG or JPEG format; here we're using PNG
      const dataUrl = canvas.toDataURL('image/png');
      callback(dataUrl);
    };
    img.src = dataUrl;
  }

  creatListPDF(receivingReports, logoUri) {
    let totalUnits :number = 0;
    let totalCost :number = 0;

    const tableData :any[] = [
      [
        { text: 'Trans. #', bold: true },
        { text: 'Item Type', bold: true },
        { text: 'Received Date', bold: true },
        { text: 'Received By', bold: true },
        { text: 'SKU', bold: true },
        { text: 'Product Name', bold: true },
        { text: 'Product Dept', bold: true },
        { text: 'Unit Price', bold: true },
        { text: 'Received Qty', bold: true },
        { text: 'Line Total', bold: true },
        { text: 'Total Units', bold: true },
        { text: 'Total Cost', bold: true },
        { text: 'Tag(s)', bold: true },
      ],
    ];

    receivingReports.forEach((rr :any) => {
      totalUnits += rr.total_quantity;
      totalCost += rr.total_cost;

      const rrDate = this.datePipe.transform(rr.received_date, 'MM/dd/yyyy') || '';

      // Product lines
      if (rr.receiving_report_rfq) {
        rr.receiving_report_rfq.forEach((rfq :any) => {
          if (rfq.purchase_order_rfq) {
            let rrPoTags :string[] = [];
            try {
              rfq.tags.forEach((tag) => rrPoTags.push(tag.name));
            } catch {
              rrPoTags = [''];
            }

            tableData.push([
              { text: rr.id.toString() },
              { text: 'Product' },
              { text: rrDate },
              { text: rr.received_by },
              { text: rfq.purchase_order_rfq.internal_sku },
              { text: rfq.purchase_order_rfq.product_name },
              {
                text: rfq.purchase_order_rfq.rfq_dept
                  ? rfq.purchase_order_rfq.rfq_dept.product_dept
                  : '',
              },
              {
                text: this.curPipe.transform(rfq.unit_price, ''),
                alignment: 'right',
              },
              { text: rfq.quantity, alignment: 'right' },
              {
                text: this.curPipe.transform(rfq.line_total, ''),
                alignment: 'right',
              },
              { text: rr.total_quantity, alignment: 'right' },
              {
                text: this.curPipe.transform(rr.total_cost, ''),
                alignment: 'right',
              },
              { text: rrPoTags.join(', ') },
            ]);
          }
        });
      }

      // Extra cost lines
      if (rr.extra_costs && Array.isArray(rr.extra_costs)) {
        rr.extra_costs.forEach((cost :any) => {
          tableData.push([
            { text: rr.id.toString() },
            { text: 'Extra Cost' },
            { text: rrDate },
            { text: rr.received_by },
            { text: '' },
            {
              text: `${cost.name} - ${cost.cost_allocation_type}`,
            },
            { text: '' },
            {
              text: this.curPipe.transform(cost.unit_price, ''),
              alignment: 'right',
            },
            { text: '', alignment: 'right' },
            {
              text: this.curPipe.transform(cost.line_total, ''),
              alignment: 'right',
            },
            {
              text: DecimalPlacesUtils.format(cost.quantity),
              alignment: 'right',
            },
            {
              text: this.curPipe.transform(cost.line_total, ''),
              alignment: 'right',
            },
            { text: '' },
          ]);
        });
      }
    });

    const headerContent = [
      {
        columns: [
          {
            alignment: 'left',
            image: logoUri,
            width: 250,
            style: 'logo',
          },
          [
            {
              text: 'Receiving Report',
              alignment: 'right',
              style: 'header',
            },
          ],
        ],
      },
    ];

    const footerContent = [
      {
        columns: [
          { text: 'Total Units:', alignment: 'right' },
          {
            text: this.curPipe.transform(totalUnits, ' ', 'symbol', '4.0'),
            alignment: 'right',
            width: 120,
          },
        ],
      },
      {
        columns: [
          { text: 'Total Cost:', alignment: 'right' },
          {
            text: this.curPipe.transform(totalCost),
            alignment: 'right',
            width: 120,
          },
        ],
      },
    ];

    const tableContent :any[] = [
      {
        text: 'Receiving Report',
        alignment: 'left',
        style: 'header',
      },
      {
        style: 'table',
        table: {
          widths: [
            'auto', // Transaction #
            'auto', // Item Type
            'auto', // Received Date
            'auto', // Received By
            'auto', // SKU
            '*', // Product Name
            'auto', // Product Dept
            'auto', // Unit Price
            'auto', // Received Qty
            'auto', // Line Total
            'auto', // Total Units
            'auto', // Total Cost
            'auto', // Tag(s)
          ],
          headerRows: 1,
          body: tableData,
        },
      },
    ];

    const pdfContent = [...headerContent, ...tableContent, ...footerContent];

    const documentDefinition = {
      pageSize: 'letter',
      pageOrientation: 'landscape',
      content: pdfContent,
      defaultStyle: {
        fontSize: 10,
        columnGap: 20,
      },
      styles: {
        logo: {
          fillColor: '#ee0000',
        },
        product: {
          fontSize: 15,
          margin: [0, 20, 0, 5],
          bold: true,
        },
        category: {
          fontSize: 14,
        },
        shipping: {
          margin: [0, 25, 0, 0],
        },
        header: {
          fontSize: 16,
          bold: false,
          margin: [0, 30, 0, 5],
          padding: [0, 0, 0, 0],
        },
        subheader: {
          fontSize: 14,
          bold: false,
          margin: [0, 0, 0, 0],
          padding: [0, 0, 0, 0],
        },
        table: {
          margin: [0, 5, 0, 15],
        },
        tableHeader: {
          bold: true,
          fontSize: 13,
          color: 'black',
        },
      },
    };

    Swal.close();
    pdfMake.createPdf(documentDefinition).download('ReceivingReport.pdf');
  }

  getImgDataUrlValue(imgUrl :string, callback) {
    const xhr = new XMLHttpRequest();
    xhr.onload = function () {
      const reader = new FileReader();
      reader.onloadend = function () {
        callback(reader.result);
      };
      reader.readAsDataURL(xhr.response);
    };
    xhr.open('GET', imgUrl);
    xhr.responseType = 'blob';
    xhr.send();
  }
}
